import React, {useRef} from 'react';


import {ToggleButton, Anchor, Icon, LineClamp, QuestionSVG, SearchSVG} from '@newsela/angelou';
import {actionNames, objectTypes, withTrackEvent} from '@newsela/snitch';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';

import {
  flexColumnCss,
  flexRowCss
} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout/SearchSuggestionsPopout.styles';
import {
  getHighlightedSuggestionText
} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout/SearchSuggestionsPopout.utils';
import {getUserProperties} from 'static/three-oh/src/modules/selectors/userSelectors';

import {
  essentialQuestionTextCss,
  footerSectionCss,
  searchSuggestionAnchorCss,
  searchSuggestionIconCss,
  suggestionsUnorderedListCss,
  toggleSwitchContainerCss
} from './SuggestionsList.styles';

export const SEARCH_WITH_NEEDLE_URL = '/search?needle=';

export const isQuestionSuggestion = (category) => category === 'eq';

const ToggleWithTrackEvent = withTrackEvent(ToggleButton);

export const focusSuggestion = ({suggestionsListRef, index, increment}) => {
  if (!suggestionsListRef.current) {
    return;
  }

  const suggestionAnchors = Array.from(
    suggestionsListRef.current.querySelectorAll('li > a')
  );

  const targetIndex = increment ? index + 1 : index - 1;
  suggestionAnchors[targetIndex]?.focus();
};

const propTypes = {
  inputValue: PropTypes.string.isRequired,
  suggestions: PropTypes.array.isRequired,
  setShowSuggestions: PropTypes.func.isRequired,
  pressed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const SuggestionsList = ({inputValue, suggestions, setShowSuggestions, pressed, onToggle}) => {
  const {push} = useHistory();
  const lunaToggleRef = useRef(null);
  const suggestionsListRef = useRef(null);


  const {canSeeAiFeatures, getExperimentSegment} = useSelector((state) => getUserProperties(state));
  const isAiSmartSearchExperimentOn = getExperimentSegment('ai_assisted_search') === 'on';
  const canSeeAiSmartSearch = canSeeAiFeatures && isAiSmartSearchExperimentOn;

  const onKeyDown = (event, suggestion, index) => {
    if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
    if (event.key === 'Enter') {
      setShowSuggestions(false);
      push(`/search/?needle=${suggestion}`);
    }
    const isLastSuggestion = index === suggestions.length - 1;
    const isNotLastSuggestion = index < suggestions.length - 1;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (isLastSuggestion && canSeeAiSmartSearch) {
        lunaToggleRef.current?.focus();
      } else if (isNotLastSuggestion) {
        focusSuggestion({suggestionsListRef, index, increment: true});
      }
    }
    const isNotFirstSuggestion = index >= 0;
    if (event.key === 'ArrowUp' && isNotFirstSuggestion) {
      event.preventDefault();
      focusSuggestion({suggestionsListRef, index, increment: false});
    }
  };

  const onLunaToggleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (suggestionsListRef.current) {
        const suggestionAnchors = Array.from(
          suggestionsListRef.current.querySelectorAll('li > a')
        );
        const lastSuggestionIndex = suggestions.length - 1;
        (suggestionAnchors[lastSuggestionIndex])?.focus();
      }
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <ul ref={suggestionsListRef} className={suggestionsUnorderedListCss}>
      {suggestions.map((s, index) => {
        let suggestion;
        if (typeof s === 'object') {
          suggestion = s.suggestion;
        } else {
          suggestion = s;
        }
        const isQuestion = s?.category ? isQuestionSuggestion(s?.category) : null;
        return (
          <li key={index}>
            <Anchor
              href={`${SEARCH_WITH_NEEDLE_URL}${suggestion}`}
              onKeyDown={(event) => onKeyDown(event, suggestion, index)}
              onClick={() => setShowSuggestions((show) => !show)}
              __cssFor={searchSuggestionAnchorCss}
              __classNameFor={{root: 'search-suggestion-anchor'}}
            >
              {isQuestion
                ? <Icon SvgComponent={QuestionSVG} __cssFor={searchSuggestionIconCss} />
                : <Icon SvgComponent={SearchSVG} __cssFor={searchSuggestionIconCss} />}

              <div className={flexColumnCss}>
                <div className={flexRowCss}>
                  {isQuestion
                    ? <div data-testid="question-text"><LineClamp lines={2} text={suggestion} /></div>
                    : <div data-testid="highlighted-text">{getHighlightedSuggestionText(suggestion, inputValue)}</div>}
                </div>

                {isQuestion && <div className={essentialQuestionTextCss}>Essential question</div>}
              </div>
            </Anchor>
          </li>
        );
      })}
      {canSeeAiSmartSearch && (
        <div
          ref={lunaToggleRef}
          className={footerSectionCss}
          tabIndex={0}
          onKeyDown={onLunaToggleKeyDown}
        >
          <ToggleWithTrackEvent
            label="Search with Luna"
            __cssFor={{
              switchContainerStyles: pressed ? toggleSwitchContainerCss : '',
            }}
            dataProps={{
              'data-qa-selector': 'enable-search-with-luna-toggle',
            }}
            objectType={objectTypes.user}
            actionName={actionNames.interact}
            streamProperties={{
              description: 'Toggle Search with Luna',
            }}
            pressed={pressed}
            onClick={onToggle}
          />
        </div>
      )}
    </ul>
  );
};

SuggestionsList.propTypes = propTypes;
SuggestionsList.displayName = 'SuggestionsList';
export {SuggestionsList};
